import { createContext, useContext } from "react"
import type { ElementRegistry } from "./element-registry"
import type { IslandRegistry } from "./island-registry"
import type { CogoBlockDef } from "./types"
import type { DesignTokens } from "../runtime/design-tokens"

export interface RenderContextValue {
  registry: ElementRegistry
  resolveSymbol: (expr: string) => unknown
  dispatchEvent: (elementId: string, eventType: string, payload?: unknown) => void
  /** Returns whether the packaged events artifact binds an element event.
   * Renderers use this to expose custom controls only when they are actually
   * interactive, while still supporting events.cogo.json bindings that are
   * intentionally separate from the UI tree. */
  hasEventBinding?: (elementId: string, eventType: string) => boolean
  /** Direct programmatic navigation (e.g., from auto-generated sidebar nav) */
  navigate?: (route: string) => void
  mode: "preview" | "edit"
  blocks?: Record<string, CogoBlockDef>
  itemContext?: Record<string, unknown>
  /** Runtime-only suffix for one concrete instance of an ontology element
   * rendered from an iteration template. The authored element ID remains the
   * event/edit authority; this suffix only makes DOM readback identities
   * unique when the same template is materialized more than once. */
  iterationIdentitySuffix?: string
  designTokens?: DesignTokens
  /** Optional host-declared map of `type: "island"` names → React components.
   * When absent, the `IslandRenderer` warns and renders null — so apps that
   * don't use islands incur no runtime change. */
  islands?: IslandRegistry
  /** Optional host hook: a media element renderer (`type: "video"`) registers
   * its live `<video>` here so the `mediaControl` ActionFlow action can
   * imperatively drive it (play/pause/seek/volume). Populated by AppV2 from
   * `hostServices.mediaRegistry`; absent in headless/standalone renders, where
   * the element still renders but imperative control is a no-op. Returns an
   * unregister function (called on unmount). Structurally compatible with
   * renderer-v2's `MediaRegistry.register` — kept as a plain function here so
   * `@cogo/react-renderer` takes no dependency on renderer-v2 (no import cycle). */
  registerMedia?: (blockId: string, element: HTMLVideoElement) => () => void
  /** Optional host hook: deliver a named-ActionFlow invocation request from a
   * mounted island (e.g. a game island's `onFlow(flowBinding, eventType)` prop)
   * to the host runtime, which resolves the named flow and executes it. The
   * `IslandRenderer` injects this onto an island's `onFlow` prop UNLESS the node
   * already set its own (JSON props can't carry a function, so this is the sole
   * delivery path). Populated by AppV2 (always — its ActionFlowEngine resolver,
   * or a host `onIslandFlow` override). Absent when a host mounts the renderer
   * WITHOUT setting it — the bare `@cogo/react-renderer` path, the studio
   * `BlockHost` shell (use-block-host builds no onFlow), and headless/standalone
   * renders — where the island's `onFlow` prop stays undefined (a no-op, as
   * today). Mirrors the `registerMedia` host-hook convention — kept as a plain
   * function so `@cogo/react-renderer` takes no renderer-v2 dependency (no import
   * cycle). */
  onFlow?: (flowName: string, eventType: string, payload?: unknown) => void
  /** Optional app-owned portal target. Studio passes the AppSandbox portal
   * container here so dialog/popover/select content inherits app tokens without
   * writing app design variables onto the Studio document root/body. */
  portalContainer?: HTMLElement | ShadowRoot | null
}

export const RenderContext = createContext<RenderContextValue | null>(null)

export function useRenderContext(): RenderContextValue {
  const ctx = useContext(RenderContext)
  if (!ctx) throw new Error("RenderContext.Provider is missing")
  return ctx
}

export function useOptionalRenderContext(): RenderContextValue | null {
  return useContext(RenderContext)
}

export function useCogoPortalContainer(): HTMLElement | ShadowRoot | null {
  return useOptionalRenderContext()?.portalContainer ?? null
}
